import React from "react";

import DatePicker from "./DatePicker";
import { type GenericFiltersMap } from "utils/filterUtils";

type PopupPosition =
  | "top left"
  | "top right"
  | "bottom left"
  | "bottom right"
  | "right center"
  | "left center"
  | "top center"
  | "bottom center"
  | undefined;

type DateRangeFilter = {
  from: string;
  to: string;
  [key: string]: string;
};

interface FilterDatePickerProps<T extends string> {
  filter: GenericFiltersMap<T>;
  updateFilter: (filter: GenericFiltersMap<T>) => void;
  propertyName: T;
  placeholder: string;
  verticalPosition?: string;
  horizontalPosition?: string;
  disabled?: boolean;
  popupPosition?: PopupPosition;
  minDate?: string;
  maxDate?: string;
}

function FilterDatePicker<T extends string>({
  filter,
  propertyName,
  placeholder,
  disabled,
  verticalPosition,
  horizontalPosition,
  popupPosition,
  minDate,
  maxDate,
  updateFilter,
}: FilterDatePickerProps<T>) {
  const handleFilterChange = (_: unknown, { value }: { value: string }) => {
    let newFilter = { ...filter };
    const lowerPlaceholder = placeholder.toLowerCase() as "from" | "to";

    if (value.length !== 0) {
      if (!newFilter[propertyName]) {
        newFilter[propertyName] = {} as DateRangeFilter;
      }

      const currentFilter = newFilter[propertyName] as DateRangeFilter;
      const updatedRangeFilter = {
        ...currentFilter,
        [lowerPlaceholder]: value,
      };

      newFilter = {
        ...newFilter,
        [propertyName]: updatedRangeFilter,
      };
    } else {
      const currentFilter = newFilter[propertyName] as DateRangeFilter;
      if (currentFilter) {
        delete currentFilter[lowerPlaceholder];

        if (!currentFilter.to && !currentFilter.from) {
          delete newFilter[propertyName];
        }
      }
    }

    updateFilter(newFilter);
  };

  const filterValue = filter[propertyName] as DateRangeFilter | undefined;
  const lowerPlaceholder = placeholder.toLowerCase();
  const value = filterValue ? filterValue[lowerPlaceholder] || "" : "";

  return (
    <DatePicker
      value={value}
      placeholder={placeholder}
      onChangeDate={handleFilterChange}
      disabled={disabled}
      verticalPosition={verticalPosition}
      horizontalPosition={horizontalPosition}
      popupPosition={popupPosition}
      minDate={minDate}
      maxDate={maxDate}
    />
  );
}

export default FilterDatePicker;
